import { Injectable } from "@angular/core";
import {
	AssignmentDefinition,
	AssignmentSourceContext,
	ManifestAssignment,
	ManifestOrigin,
} from "../assignment.interface";
import { ApiService } from "source/app/configuration/services/api.service";
import { TranslocoService } from "@ngneat/transloco";
import { ActionSheetController } from "@ionic/angular";
import { AssignmentTrigger, AssignmentWithData, Checklist } from "../../checklists/checklist.interface";
import { LocationStateQuery } from "source/app/configuration/state/location-state.query";
import { ChecklistProgressStateQuery } from "../../checklists/state/checklist-progress-state.query";
import { addDays, addHours } from "date-fns";
import { AssignmentModalService } from "../modals/assignment-modal.service";
import { getDateTimeForDateTimeExtensionPolicy } from "source/app/configuration/date-time-extension.utils";

@Injectable({ providedIn: "root" })
export class AssignmentService {
	constructor(
		private apiService: ApiService,
		private translocoService: TranslocoService,
		private actionSheetController: ActionSheetController,
		private locationStateQuery: LocationStateQuery,
		private checklistProgressQuery: ChecklistProgressStateQuery,
		private assignmentModalService: AssignmentModalService,
	) {}

	createAssignment = async (questionId: string, definition: AssignmentDefinition) => {
		const location = await this.locationStateQuery.location$.firstAsync();
		const checklist = await this.checklistProgressQuery.checklist$.firstAsync();

		const initialState = definition?.states.find((x) => x.type === "Initial");
		const initialPriority = definition?.priorities.find((x) => x.default);

		const origin: ManifestOrigin = {
			checklist: checklist.id,
			question: questionId,
			schedule: location.id,
			option: null,
			triggerCondition: null,
		};

		const start = new Date().toISOString();
		const end = await this.getAssignmentEndDateIsoString(start, definition);

		const assignment: ManifestAssignment = {
			organization: {
				id: checklist.organization,
				name: "",
			},
			target: {
				id: location.id,
				name: location.name,
			},
			asset: null,
			assignees: [],
			attachments: [],
			origin: origin,
			description: "",
			definition: definition,
			definitionState: initialState,
			definitionPriority: initialPriority,
			start: start,
			end: end,
		};

		return this.assignmentModalService.open(
			assignment,
			definition,
			null,
			null,
			this.buildSourceContext(checklist, location.name, questionId),
		);
	};

	createAssignmentFromTrigger = async (trigger: AssignmentTrigger, questionId: string) => {
		const checklist = await this.checklistProgressQuery.checklist$.firstAsync();
		const definition: AssignmentDefinition = checklist.references[trigger.definition];
		const initialState = definition?.states.find((x) => x.type === "Initial");
		const definitionPriority = definition?.priorities?.find((x) => x.id === trigger.definitionPriority);
		const location = await this.locationStateQuery.location$.firstAsync();
		const user = await this.locationStateQuery.operator$.firstAsync();

		const origin: ManifestOrigin = {
			checklist: checklist.id,
			question: questionId,
			schedule: location.id,
			option: trigger.condition.right,
			triggerCondition: trigger.condition,
		};

		const start = new Date().toISOString();
		const end = await this.getAssignmentEndDateIsoString(start, definition, trigger);

		const assignment: ManifestAssignment = {
			organization: {
				id: checklist.organization,
				name: "",
			},
			target: {
				id: location.id,
				name: location.name,
			},
			asset: null,
			assignees: trigger.assignToPerformingUser
				? [{ id: user.id, name: user.name }]
				: trigger.assignees.map((assignee) => checklist.references[assignee]),
			categories: trigger.categories,
			attachments: [],
			origin: origin,
			fromTrigger: true,
			description: "",
			definition: definition,
			definitionState: initialState,
			priority: trigger.priority,
			definitionPriority: definitionPriority,
			subscribers: trigger.subscribers,
			start: start,
			name: trigger.name,
			end: end,
		};

		if (trigger.requireNoIntervention) {
			const assignmentWithData: AssignmentWithData = {
				assignment: assignment,
				solution: null,
				triggeredBy: trigger,
			};

			return assignmentWithData;
		}

		return await this.assignmentModalService.open(
			assignment,
			definition,
			trigger,
			null,
			this.buildSourceContext(checklist, location.name, questionId),
		);
	};

	openAssignment = async (assignment: AssignmentWithData): Promise<AssignmentWithData> => {
		const definitions = await this.getDefinitions();
		const definition = definitions.find((x) => x.id === assignment.assignment.definition?.id);
		const checklist = await this.checklistProgressQuery.checklist$.firstAsync();
		const questionId = assignment.assignment.origin?.question;
		const locationName = assignment.assignment.target?.name ?? "";

		return this.assignmentModalService.open(
			assignment.assignment,
			definition,
			assignment.triggeredBy,
			assignment.solution,
			questionId ? this.buildSourceContext(checklist, locationName, questionId) : null,
		);
	};

	private buildSourceContext = (
		checklist: Checklist,
		locationName: string,
		questionId: string,
	): AssignmentSourceContext => {
		const checklistName = checklist.name?.[checklist.language] ?? Object.values(checklist.name ?? {})[0] ?? "";
		let questionText = "";
		for (const page of checklist.pages ?? []) {
			const match = page.elements?.find((el) => el.id === questionId);
			if (match && "translatedText" in match) {
				questionText = match.translatedText || match.text?.[checklist.language] || "";
				break;
			}
		}

		return {
			locationName,
			checklistName,
			questionText,
		};
	};

	private getAssignmentEndDateIsoString = async (
		start: string | Date,
		definition: AssignmentDefinition,
		trigger?: AssignmentTrigger,
	): Promise<string> => {
		if (trigger?.deadline) {
			return trigger.deadlineUnit === "Days"
				? addDays(start, trigger.deadlineAmount).toISOString()
				: addHours(start, trigger.deadlineAmount).toISOString();
		}

		const dateTimeSettings = await this.locationStateQuery.organizationDateTimeSettings$.firstAsync();
		return getDateTimeForDateTimeExtensionPolicy(
			start,
			definition?.deadlineExtensionPolicy,
			dateTimeSettings,
		).toISOString();
	};

	getDefinitions = async (): Promise<AssignmentDefinition[]> => {
		const assignmentDefinitionSettings = await this.apiService.assignmentDefinitionSettings();
		return assignmentDefinitionSettings.definitions;
	};

	selectDefinition = async (): Promise<AssignmentDefinition> => {
		const assignmentDefinitionSettings = await this.apiService.assignmentDefinitionSettings();

		// No Assignment definitions exists
		if (assignmentDefinitionSettings.definitions.length == 0) {
			return null;
		}

		// Only one assignment definition is available
		if (
			assignmentDefinitionSettings.definitions.length == 1 &&
			assignmentDefinitionSettings.disableDefaultDefinition
		) {
			return assignmentDefinitionSettings.definitions[0];
		}

		const defaultDefinition = {
			text: this.translocoService.translate("ASSIGNMENT_LIST__SELECT_DEFINITION_DEFAULT"),
			data: {
				action: "",
			},
		};

		const cancelButton = {
			text: this.translocoService.translate("BASE__CANCEL"),
			role: "cancel",
			data: {
				action: "cancel",
			},
		};

		const definitionButtons = assignmentDefinitionSettings.definitions.map((definition) => {
			return {
				text: definition.name,
				data: {
					action: definition.id,
				},
			};
		});

		const buttons = assignmentDefinitionSettings.disableDefaultDefinition
			? [...definitionButtons, cancelButton]
			: [defaultDefinition, ...definitionButtons, cancelButton];

		const actionSheet = await this.actionSheetController.create({
			header: this.translocoService.translate("ASSIGNMENT_LIST__SELECT_DEFINITION_TITLE"),
			mode: "ios",
			buttons: buttons,
		});

		await actionSheet.present();

		const action = await actionSheet.onWillDismiss();

		if (action.role === "cancel") {
			throw new Error("definition selection cancelled");
		}

		if (action.data.action === "") {
			return null;
		}

		return assignmentDefinitionSettings.definitions.find((x) => x.id === action.data.action);
	};
}
